import { Injectable, signal, computed, inject, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Observable,
  tap,
  catchError,
  throwError,
  of,
  switchMap,
  take,
  map,
} from 'rxjs';
import {
  LoginRequest,
  LoginResponse,
  AuthUser,
  ApiResponse,
  getAdminPermissions,
} from '@shared-models';
import {
  buildCrossAppHandoffUrl,
  buildCrossAppPlainUrl,
  readCrossAppCodeFromLocation,
} from './auth-cross-app.handoff';
import {
  broadcastCrossAppLogout,
  collectCrossAppLogoutOrigins,
  readCrossAppLogoutFromLocation,
} from './auth-cross-app.logout';
import { API_CONFIG, ApiConfig } from './api-config';
import { GuestService } from './guest.service';
import {
  RepoSelectsService,
  type RepoSelectOption,
} from './settings/repo-selects.service';

const TOKEN_KEY = 'argenper_access_token';
const REFRESH_TOKEN_KEY = 'argenper_refresh_token';
const USER_KEY = 'argenper_user';
const SESSION_EXPIRED_MSG_KEY = 'argenper_session_expired_msg';
const SSO_LOGOUT_FLAG_KEY = 'argenper_sso_logout';

const DEFAULT_SESSION_EXPIRED_MSG =
  'No autenticado. Por favor, inicie sesión para acceder a este recurso.';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly guestService = inject(GuestService);
  private readonly repoSelectsService = inject(RepoSelectsService);
  private readonly ngZone = inject(NgZone);
  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/auth`;
  }

  // Signals para estado reactivo
  private readonly _user = signal<AuthUser | null>(this.loadUser());
  private readonly _accessToken = signal<string | null>(this.loadToken());
  private readonly _refreshToken = signal<string | null>(
    this.loadRefreshToken(),
  );
  /**
   * Mensaje de sesión expirada / 401. El layout lo muestra en un modal
   * global; al aceptar se navega a `/login`.
   */
  private readonly _sessionExpiredMessage = signal<string | null>(null);

  // Computed signals públicos de solo lectura
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._accessToken());
  readonly userRoles = computed(() => this._user()?.roles ?? []);
  readonly sessionExpiredMessage = this._sessionExpiredMessage.asReadonly();

  // Permissions - combinar permisos del usuario con permisos de admin si es admin
  readonly userPermissions = computed(() => {
    const user = this._user();
    if (!user) return [];

    const userPerms = user.permissions ?? [];
    const isAdmin = user.roles?.some(
      (r: string) =>
        r.toLowerCase().includes('admin') || r.toLowerCase().includes('super'),
    );

    // Si es admin, agregar todos los permisos de admin
    if (isAdmin) {
      const adminPerms = getAdminPermissions();
      return [...new Set([...userPerms, ...adminPerms])];
    }

    return userPerms;
  });

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.bindCrossTabSessionSync();
  }

  login(credentials: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    // Obtener guest token primero y luego hacer login con él
    return this.guestService.getGuestToken().pipe(
      switchMap((guestResp) => {
        const guestToken = guestResp.data?.token;

        // Preparar headers
        let headers = new HttpHeaders();
        if (guestToken) {
          headers = headers.set('X-Guest-Token', guestToken);
        }

        // Ejecutar la petición
        return this.http
          .post<
            ApiResponse<LoginResponse>
          >(`${this.apiUrl}/authenticate`, credentials, { headers })
          .pipe(
            tap((response) => {
              if (response.success && response.data) {
                this.storeAuth(response.data);
              }
            }),
            catchError((error) => {
              return throwError(() => error);
            }),
          );
      }),
    );
  }

  logout(): void {
    const accessToken = this._accessToken();
    const refreshToken = this._refreshToken();

    this.markSsoLogoutFlag();
    this.pingOtherRepoSessions();

    // Llamar al backend para invalidar los tokens del lado del servidor
    if (accessToken || refreshToken) {
      this.http
        .post<ApiResponse<void>>(`${this.apiUrl}/logout`, {
          accessToken: accessToken,
          refreshToken: refreshToken,
        })
        .pipe(
          catchError((error) => {
            // Continuar con el logout local incluso si falla el llamado al backend
            console.warn(
              'Backend logout failed, continuing with local logout:',
              error,
            );
            return of(null);
          }),
        )
        .subscribe();
    }

    this.clearLocalSession();
    this._sessionExpiredMessage.set(null);
    this.router.navigate(['/login']);
  }

  /**
   * Limpia tokens/usuario en memoria y localStorage sin navegar.
   */
  clearLocalSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._user.set(null);
    this._accessToken.set(null);
    this._refreshToken.set(null);
  }

  /**
   * Respuesta 401 de la API (sesión inválida/expirada).
   * Limpia la sesión y deja el mensaje listo para el modal global;
   * la redirección a login ocurre al aceptar (`acknowledgeSessionExpired`).
   */
  handleSessionExpired(message?: string | null): void {
    // Evitar re-abrir el modal si ya estamos en el flujo de sesión expirada.
    if (this._sessionExpiredMessage()) {
      this.clearLocalSession();
      return;
    }

    this.clearLocalSession();
    const msg =
      (typeof message === 'string' && message.trim()) ||
      DEFAULT_SESSION_EXPIRED_MSG;
    try {
      sessionStorage.setItem(SESSION_EXPIRED_MSG_KEY, msg);
    } catch {
      // sessionStorage puede fallar en modo privado; el signal alcanza.
    }
    this._sessionExpiredMessage.set(msg);
  }

  /**
   * Cierra el modal de sesión expirada y navega al login.
   */
  acknowledgeSessionExpired(): void {
    this._sessionExpiredMessage.set(null);
    try {
      sessionStorage.removeItem(SESSION_EXPIRED_MSG_KEY);
    } catch {
      // ignore
    }
    this.router.navigate(['/login']);
  }

  /**
   * Mensaje pendiente de sesión expirada (p.ej. si el guard redirigió
   * a login antes de mostrar el modal del layout).
   */
  consumePendingSessionExpiredMessage(): string | null {
    const fromSignal = this._sessionExpiredMessage();
    if (fromSignal) {
      this._sessionExpiredMessage.set(null);
      try {
        sessionStorage.removeItem(SESSION_EXPIRED_MSG_KEY);
      } catch {
        // ignore
      }
      return fromSignal;
    }
    try {
      const stored = sessionStorage.getItem(SESSION_EXPIRED_MSG_KEY);
      if (stored) {
        sessionStorage.removeItem(SESSION_EXPIRED_MSG_KEY);
        return stored;
      }
    } catch {
      // ignore
    }
    return null;
  }

  refreshToken(): Observable<ApiResponse<LoginResponse>> {
    const currentRefreshToken = this._refreshToken();
    if (!currentRefreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.apiUrl}/refresh`, {
        refreshToken: currentRefreshToken,
      })
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            this.storeAuth(response.data);
          }
        }),
        catchError((error) => {
          this.clearLocalSession();
          return throwError(() => error);
        }),
      );
  }

  getAccessToken(): string | null {
    this.dropMemoryIfRemoteLogout();
    return this._accessToken();
  }

  hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    const userRoles = this.userRoles();
    return roles.some((role) => userRoles.includes(role));
  }

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    return this.userPermissions().includes(permission);
  }

  /**
   * Verificar si el usuario tiene alguno de los permisos especificados
   */
  hasAnyPermission(...permissions: string[]): boolean {
    const perms = this.userPermissions();
    return permissions.some((p) => perms.includes(p));
  }

  /**
   * Verificar si el usuario tiene acceso a un sistema específico
   */
  hasSystemAccess(systemPermission: string): boolean {
    return this.hasPermission(systemPermission);
  }

  /**
   * URL hacia otra app del monorepo (sin tokens). El código SSO se pide al navegar.
   */
  buildCrossAppUrl(targetBaseUrl: string, entryPath = '/app'): string {
    return buildCrossAppPlainUrl(targetBaseUrl, entryPath);
  }

  /**
   * Crea código one-time en el API y redirige a la app destino con `#ap_code=...`.
   */
  navigateToCrossApp(targetUrl: string): void {
    if (typeof window === 'undefined' || !targetUrl) return;

    let targetOrigin: string;
    let entryPath: string;
    try {
      const url = new URL(targetUrl, window.location.origin);
      targetOrigin = url.origin;
      entryPath = `${url.pathname}${url.search}` || '/app';
    } catch {
      window.location.href = targetUrl;
      return;
    }

    if (targetOrigin === window.location.origin) {
      window.location.href = targetUrl;
      return;
    }

    if (!this._accessToken()) {
      window.location.href = buildCrossAppPlainUrl(targetOrigin, entryPath);
      return;
    }

    this.createHandoffCode(targetOrigin)
      .pipe(take(1))
      .subscribe({
        next: (code) => {
          window.location.href = buildCrossAppHandoffUrl(
            targetOrigin,
            entryPath,
            code,
          );
        },
        error: () => {
          // Sin código: caer a la app destino (pedirá login si no hay sesión local).
          window.location.href = buildCrossAppPlainUrl(targetOrigin, entryPath);
        },
      });
  }

  /** POST /auth/handoff → código one-time. */
  createHandoffCode(targetOrigin?: string | null): Observable<string> {
    const body =
      targetOrigin && targetOrigin.trim()
        ? { targetOrigin: targetOrigin.trim() }
        : {};
    return this.http
      .post<ApiResponse<{ code: string; expiresIn: number }>>(
        `${this.apiUrl}/handoff`,
        body,
      )
      .pipe(
        map((res) => {
          const code = res?.data?.code?.trim();
          if (!res?.success || !code) {
            throw new Error(res?.message || 'No se pudo generar handoff');
          }
          return code;
        }),
      );
  }

  /**
   * Consume `#ap_code=...` al aterrizar en otra app.
   * Debe resolverse antes de evaluar authGuard / publicOnlyGuard.
   */
  consumeCrossAppHandoff$(): Observable<boolean> {
    const code = readCrossAppCodeFromLocation();
    if (!code) {
      return of(false);
    }

    const targetOrigin =
      typeof window !== 'undefined' ? window.location.origin : undefined;
    return this.http
      .post<ApiResponse<LoginResponse>>(`${this.apiUrl}/handoff/exchange`, {
        code,
        targetOrigin,
      })
      .pipe(
        map((res) => {
          if (!res?.success || !res.data?.accessToken || !res.data?.user) {
            throw new Error(res?.message || 'Handoff inválido');
          }
          this.storeAuth(res.data);
          this._sessionExpiredMessage.set(null);
          return true;
        }),
        catchError(() => of(false)),
      );
  }

  /**
   * Consume `#ap_logout=1` (cierre SSO). Gana sobre el handoff de sesión.
   */
  consumeCrossAppLogout(): boolean {
    if (!readCrossAppLogoutFromLocation()) return false;
    this.clearLocalSession();
    this._sessionExpiredMessage.set(null);
    return true;
  }

  /** Alinea la sesión en memoria con un logout hecho en otro repo/pestaña. */
  syncSessionFromStorage(): void {
    this.dropMemoryIfRemoteLogout();
  }

  /** Avisa a los demás orígenes para que borren su localStorage. */
  private pingOtherRepoSessions(): void {
    broadcastCrossAppLogout(collectCrossAppLogoutOrigins([]));
    this.repoSelectsService
      .getOptions()
      .pipe(
        catchError(() => of([] as RepoSelectOption[])),
        take(1),
      )
      .subscribe((items) => {
        broadcastCrossAppLogout(
          collectCrossAppLogoutOrigins(items.map((item) => item.code)),
        );
      });
  }

  /**
   * Si otra pestaña/iframe del mismo origen borra el token, cerrar aquí también.
   */
  private bindCrossTabSessionSync(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', (event: StorageEvent) => {
      const logoutFlag = event.key === SSO_LOGOUT_FLAG_KEY && !!event.newValue;
      const tokenRemoved = event.key === TOKEN_KEY && !event.newValue;
      const storageCleared = event.key === null;
      if (!logoutFlag && !tokenRemoved && !storageCleared) return;
      this.ngZone.run(() => this.redirectIfRemoteLogout());
    });

    window.addEventListener('focus', () => {
      this.ngZone.run(() => this.redirectIfRemoteLogout());
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.ngZone.run(() => this.redirectIfRemoteLogout());
      }
    });
  }

  private markSsoLogoutFlag(): void {
    try {
      localStorage.setItem(SSO_LOGOUT_FLAG_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  }

  private hasRemoteLogoutFlag(): boolean {
    try {
      return !!localStorage.getItem(SSO_LOGOUT_FLAG_KEY);
    } catch {
      return false;
    }
  }

  /** Quita la sesión en memoria si otro documento de este origen ya cerró. */
  private dropMemoryIfRemoteLogout(): boolean {
    if (!this._accessToken()) return false;
    if (!this.hasRemoteLogoutFlag() && this.loadToken()) return false;
    this.clearLocalSession();
    this._sessionExpiredMessage.set(null);
    return true;
  }

  private redirectIfRemoteLogout(): void {
    if (!this.dropMemoryIfRemoteLogout()) return;
    if (!this.router.url.startsWith('/login')) {
      void this.router.navigate(['/login']);
    }
  }

  private storeAuth(data: LoginResponse): void {
    try {
      localStorage.removeItem(SSO_LOGOUT_FLAG_KEY);
    } catch {
      // ignore
    }
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    this._accessToken.set(data.accessToken);
    this._refreshToken.set(data.refreshToken);
    this._user.set(data.user);
  }

  private loadToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private loadRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}
